import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  Badge,
  Avatar,
  IconButton,
  Switch,
  FormControlLabel,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
} from '@mui/material';

// Icons
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MailIcon from '@mui/icons-material/Mail';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';

/**
 * Design System Showcase Page
 * Demonstrates all MUI components with KARSA NUSA theme
 */
export default function DesignSystemShowcase() {
  const [openDialog, setOpenDialog] = useState(false);
  const [progress, setProgress] = useState(50);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h1" gutterBottom>
        KARSA NUSA Design System
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        A comprehensive showcase of Material UI components with our custom theme
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* Color Palette */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Color Palette
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              <Typography variant="h6">Primary</Typography>
              <Typography variant="body2">#605DFF</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, bgcolor: 'secondary.main', color: 'secondary.contrastText' }}>
              <Typography variant="h6">Secondary</Typography>
              <Typography variant="body2">#AD63F6</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, bgcolor: 'accent.main', color: 'accent.contrastText' }}>
              <Typography variant="h6">Accent</Typography>
              <Typography variant="body2">#37cdbe</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, bgcolor: 'success.main', color: 'success.contrastText' }}>
              <Typography variant="h6">Success</Typography>
              <Typography variant="body2">#36d399</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
              <Typography variant="h6">Warning</Typography>
              <Typography variant="body2">#fbbd23</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, bgcolor: 'error.main', color: 'error.contrastText' }}>
              <Typography variant="h6">Error</Typography>
              <Typography variant="body2">#f87272</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, bgcolor: 'info.main', color: 'info.contrastText' }}>
              <Typography variant="h6">Info</Typography>
              <Typography variant="body2">#3abff8</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 6 }} />

      {/* Typography */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Typography
        </Typography>
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h1">Heading 1 - Page Title</Typography>
          <Typography variant="h2">Heading 2 - Section Header</Typography>
          <Typography variant="h3">Heading 3 - Subsection</Typography>
          <Typography variant="h4">Heading 4 - Card Title</Typography>
          <Typography variant="h5">Heading 5 - Item Title</Typography>
          <Typography variant="h6">Heading 6 - Overline</Typography>
          <Typography variant="body1">
            Body 1 - Main content text. This is the standard text style for paragraphs and content.
          </Typography>
          <Typography variant="body2">
            Body 2 - Secondary text. Used for less important information.
          </Typography>
          <Typography variant="caption">Caption - Helper text and labels</Typography>
        </Paper>
      </Box>

      <Divider sx={{ my: 6 }} />

      {/* Buttons */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Buttons
        </Typography>
        <Paper sx={{ p: 3, mt: 2 }}>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Button variant="contained" color="primary">
              Primary
            </Button>
            <Button variant="contained" color="secondary">
              Secondary
            </Button>
            <Button variant="contained" color="accent">
              Accent
            </Button>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Button variant="outlined" color="primary">
              Primary
            </Button>
            <Button variant="outlined" color="secondary">
              Secondary
            </Button>
            <Button variant="outlined" color="accent">
              Accent
            </Button>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Button variant="text" color="primary">
              Primary
            </Button>
            <Button variant="text" color="secondary">
              Secondary
            </Button>
            <Button variant="text" color="accent">
              Accent
            </Button>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Button size="small" variant="contained">Small</Button>
            <Button size="medium" variant="contained">Medium</Button>
            <Button size="large" variant="contained">Large</Button>
          </Stack>
          <Stack direction="row" spacing={2}>
            <IconButton color="primary">
              <EditIcon />
            </IconButton>
            <IconButton color="secondary">
              <DeleteIcon />
            </IconButton>
            <IconButton color="info">
              <MailIcon />
            </IconButton>
          </Stack>
        </Paper>
      </Box>

      <Divider sx={{ my: 6 }} />

      {/* Form Elements */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Form Elements
        </Typography>
        <Paper sx={{ p: 3, mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Standard Input"
                variant="outlined"
                fullWidth
                placeholder="Enter text"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="With Helper Text"
                variant="outlined"
                fullWidth
                helperText="This is helper text"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Error State"
                variant="outlined"
                fullWidth
                error
                helperText="Invalid input"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Disabled"
                variant="outlined"
                fullWidth
                disabled
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Divider sx={{ my: 6 }} />

      {/* Alerts */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Alerts
        </Typography>
        <Paper sx={{ p: 3, mt: 2 }}>
          <Stack spacing={2}>
            <Alert severity="success" icon={<CheckCircleIcon />}>
              Success! Operation completed successfully.
            </Alert>
            <Alert severity="error" icon={<ErrorIcon />}>
              Error! Something went wrong.
            </Alert>
            <Alert severity="warning" icon={<WarningIcon />}>
              Warning! Please review before continuing.
            </Alert>
            <Alert severity="info" icon={<InfoIcon />}>
              Info! Here's some useful information.
            </Alert>
          </Stack>
        </Paper>
      </Box>

      <Divider sx={{ my: 6 }} />

      {/* Chips */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Chips & Badges
        </Typography>
        <Paper sx={{ p: 3, mt: 2 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            <Chip label="Default" />
            <Chip label="Primary" color="primary" />
            <Chip label="Secondary" color="secondary" />
            <Chip label="Success" color="success" />
            <Chip label="Error" color="error" />
            <Chip label="Warning" color="warning" />
            <Chip label="Info" color="info" />
          </Stack>
          <Stack direction="row" spacing={2}>
            <Badge badgeContent={4} color="primary">
              <IconButton>
                <MailIcon />
              </IconButton>
            </Badge>
            <Badge badgeContent={99} color="error">
              <IconButton>
                <NotificationsIcon />
              </IconButton>
            </Badge>
            <Badge variant="dot" color="success">
              <Avatar>JD</Avatar>
            </Badge>
          </Stack>
        </Paper>
      </Box>

      <Divider sx={{ my: 6 }} />

      {/* Cards */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Cards
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Card Title
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This is a card with content. Cards are great for displaying related information.
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small">Cancel</Button>
                <Button size="small" variant="contained">Action</Button>
              </CardActions>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2 }}>JD</Avatar>
                  <Box>
                    <Typography variant="h6">John Doe</Typography>
                    <Typography variant="caption" color="text.secondary">
                      john@example.com
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2">
                  User profile card with avatar and information.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  KPI Card
                </Typography>
                <Typography variant="h3" color="primary">
                  248
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Users (↑ 12%)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 6 }} />

      {/* Progress Indicators */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Progress Indicators
        </Typography>
        <Paper sx={{ p: 3, mt: 2 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" gutterBottom>
                Linear Progress
              </Typography>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="caption" color="text.secondary">
                {progress}%
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" gutterBottom>
                Circular Progress
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <CircularProgress size={40} />
                <CircularProgress size={40} color="secondary" />
                <CircularProgress size={40} variant="determinate" value={75} />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Divider sx={{ my: 6 }} />

      {/* Tables */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Tables
        </Typography>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>John Doe</TableCell>
                <TableCell>john@example.com</TableCell>
                <TableCell>Admin</TableCell>
                <TableCell>
                  <Chip label="Active" size="small" color="success" />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Jane Smith</TableCell>
                <TableCell>jane@example.com</TableCell>
                <TableCell>User</TableCell>
                <TableCell>
                  <Chip label="Inactive" size="small" color="default" />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Divider sx={{ my: 6 }} />

      {/* Dialogs */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Dialogs
        </Typography>
        <Paper sx={{ p: 3, mt: 2 }}>
          <Button variant="contained" onClick={() => setOpenDialog(true)}>
            Open Dialog
          </Button>
        </Paper>
      </Box>

      {/* Dialog Component */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Dialog Title</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This is a dialog with custom styling. Dialogs are used for important
            actions that require user confirmation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={() => setOpenDialog(false)} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Switches */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Switches
        </Typography>
        <Paper sx={{ p: 3, mt: 2 }}>
          <Stack spacing={2}>
            <FormControlLabel
              control={<Switch defaultChecked />}
              label="Enabled"
            />
            <FormControlLabel
              control={<Switch />}
              label="Disabled"
            />
            <FormControlLabel
              control={<Switch disabled />}
              label="Disabled (state)"
            />
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
}
